'use client';

import { useEffect, useState } from 'react';
import { FeatureFlagTypes } from '@/shared/types';

const SESSION_KEY = 'helio-demo';
const URL_PARAM = 'helio-demo';
const BODY_CLASS = 'helio-demo';
const BODY_LOGIN_CLASS = 'helio-demo-login';

function checkFlags(featureFlags?: Record<string, unknown> | null): boolean {
  if (featureFlags) {
    return !!(featureFlags.helioDemo ?? featureFlags[URL_PARAM]);
  }
  return false;
}

/** Checks URL param and sessionStorage (survives redirects, clears when tab closes). */
function checkClient(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.search.includes(URL_PARAM)) {
    window.sessionStorage.setItem(SESSION_KEY, 'true');
    return true;
  }
  return window.sessionStorage.getItem(SESSION_KEY) === 'true';
}

/**
 * Single source of truth for the helio-demo feature flag.
 *
 * Resolves from (in priority order):
 *   1. featureFlags object (when available, e.g. inside Main)
 *   2. URL search param  (?helio-demo)  — persisted to sessionStorage so it survives login redirects
 *   3. /api/features      (server env var — used when featureFlags aren't available yet)
 *
 * To remove helio-demo from the codebase:
 *   1. Delete  useHelioDemo.ts, HelioDemoBodyClass.tsx, helio-demo.css
 *   2. Remove  their imports from layout.tsx and main.tsx
 *   3. Remove  the ~6 lines in main.tsx that reference isHelioDemo
 *   4. Remove  the 'helio-demo' key from /api/features/route.ts
 *   5. Delete  public/helio-logo-white.svg and public/helio-logo-blueblack.svg
 */
export function useHelioDemo(
  featureFlags?: Record<string, FeatureFlagTypes> | null,
): boolean {
  const [fromClient, setFromClient] = useState(false);
  const [fromApi, setFromApi] = useState<boolean | null>(null);

  useEffect(() => {
    setFromClient(checkClient());
  }, []);

  useEffect(() => {
    if (featureFlags) return;
    let cancelled = false;
    fetch('/api/features', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: Record<string, unknown> | null) => {
        if (cancelled || !data) return;
        setFromApi(!!(data.helioDemo ?? data[URL_PARAM]));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [featureFlags]);

  return checkFlags(featureFlags) || fromClient || fromApi === true;
}

/** Class names used by helio-demo CSS — exported so callers don't hardcode strings. */
export const helioDemoClasses = {
  body: BODY_CLASS,
  login: BODY_LOGIN_CLASS,
  loginWrapper: 'helio-demo-login-wrapper',
} as const;
